
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  Shield, 
  RefreshCw, 
  Eye, 
  Check, 
  X, 
  User,
  Calendar,
  Phone,
  CreditCard,
  FileImage,
  Clock
} from 'lucide-react';

interface KYCProfile {
  id: string;
  full_name: string;
  email: string;
  phone?: string;
  national_id?: string;
  kyc_status: string;
  id_front_url?: string;
  id_back_url?: string;
  created_at: string;
}

const KYCManagement = () => {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const [profiles, setProfiles] = useState<KYCProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState<KYCProfile | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [updating, setUpdating] = useState<string | null>(null);

  const fetchKYCProfiles = async () => {
    try {
      console.log('Fetching KYC profiles...');
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .neq('kyc_status', 'VERIFIED')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching KYC profiles:', error);
        toast.error('Failed to fetch KYC profiles');
        return;
      }

      console.log('Fetched KYC profiles:', data?.length);
      setProfiles(data || []);
    } catch (error) {
      console.error('Exception fetching KYC profiles:', error);
      toast.error('Failed to fetch KYC profiles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKYCProfiles();

    // Set up polling to refresh data every 30 seconds
    const pollInterval = setInterval(() => {
      console.log('Polling for KYC profile updates...');
      fetchKYCProfiles();
    }, 30000);

    return () => {
      clearInterval(pollInterval);
    };
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'VERIFIED':
        return <Badge className="bg-green-500 text-white">
          <Check className="h-3 w-3 mr-1" />
          Verified
        </Badge>;
      case 'REJECTED':
        return <Badge variant="destructive">
          <X className="h-3 w-3 mr-1" />
          Rejected
        </Badge>;
      case 'UNDER_REVIEW':
        return <Badge variant="outline" className="border-blue-500 text-blue-600">
          <RefreshCw className="h-3 w-3 mr-1" />
          Under Review
        </Badge>;
      default:
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </Badge>;
    }
  };

  const updateKYCStatus = async (profileId: string, status: 'VERIFIED' | 'REJECTED') => {
    setUpdating(profileId);
    try {
      console.log('Updating KYC status using database function:', { profileId, status });
      
      // Use the proper database function that creates notifications
      const { error } = await supabase.rpc('update_kyc_status', {
        target_user_id: profileId,
        new_status: status
      });

      if (error) {
        console.error('Error updating KYC status:', error);
        toast.error('Failed to update KYC status');
        return;
      }

      toast.success(`KYC status updated to ${status.toLowerCase()}`);
      
      // Update local state
      setProfiles(prev => prev.map(p => 
        p.id === profileId ? { ...p, kyc_status: status } : p
      ));
      
      // Close dialog if the selected profile was updated
      if (selectedProfile?.id === profileId) {
        setSelectedProfile(prev => prev ? { ...prev, kyc_status: status } : null);
        setIsDialogOpen(false);
      }
    } catch (error) {
      console.error('Exception updating KYC status:', error);
      toast.error('Failed to update KYC status');
    } finally {
      setUpdating(null);
    }
  };

  const extractStoragePath = (url: string): string => {
    if (!url) return '';
    
    // Extract path from full Supabase URL
    const match = url.match(/\/storage\/v1\/object\/public\/kyc-documents\/(.+)$/);
    return match ? match[1] : url;
  };

  const viewDocument = async (url: string) => {
    if (!url) return;
    
    try {
      // Extract the storage path from the URL
      const storagePath = extractStoragePath(url);
      
      // Generate a signed URL for the private bucket
      const { data, error } = await supabase.storage
        .from('kyc-documents')
        .createSignedUrl(storagePath, 3600); // 1 hour expiry

      if (error) {
        console.error('Error creating signed URL:', error);
        throw error;
      }
      
      window.open(data.signedUrl, '_blank');
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error('Failed to view document');
    }
  };

  const handleViewProfile = (profile: KYCProfile) => {
    setSelectedProfile(profile);
    setIsDialogOpen(true);
  };

  const DocumentThumbnail = ({ url, label }: { url?: string; label: string }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
      const loadImage = async () => {
        if (!url) {
          setLoading(false);
          return;
        }

        try {
          setLoading(true);
          setError(false);

          // Extract the storage path from the URL
          const storagePath = extractStoragePath(url);
          
          // Generate a signed URL for the private bucket
          const { data, error: storageError } = await supabase.storage
            .from('kyc-documents')
            .createSignedUrl(storagePath, 3600); // 1 hour expiry

          if (storageError) {
            console.error('Error creating signed URL:', storageError);
            setError(true);
          } else {
            setImageUrl(data.signedUrl);
          }
        } catch (err) {
          console.error('Error loading image:', err);
          setError(true);
        } finally {
          setLoading(false);
        }
      };

      loadImage();
    }, [url]);

    if (!url) {
      return (
        <div className="flex flex-col items-center space-y-1">
          <div className="w-20 h-16 bg-gray-100 border-2 border-dashed border-gray-300 rounded flex items-center justify-center">
            <span className="text-xs text-gray-400">No {label}</span>
          </div>
          <span className="text-xs text-gray-600">{label}</span>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center space-y-1">
        <div 
          className="w-20 h-16 border-2 border-gray-200 rounded cursor-pointer hover:border-primary transition-colors overflow-hidden bg-white"
          onClick={() => viewDocument(url)}
          title={`Click to view ${label}`}
        >
          {loading ? (
            <div className="w-full h-full flex items-center justify-center">
              <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="w-full h-full flex items-center justify-center bg-red-50">
              <FileImage className="h-4 w-4 text-red-400" />
            </div>
          ) : imageUrl ? (
            <img 
              src={imageUrl} 
              alt={label}
              className="w-full h-full object-cover"
              onError={() => setError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FileImage className="h-4 w-4 text-gray-600" />
            </div>
          )}
        </div>
        <span className="text-xs text-gray-600">{label}</span>
        {imageUrl && !loading && !error && (
          <Badge variant="outline" className="text-xs px-1 py-0">
            <Eye className="h-2 w-2 mr-1" />
            View
          </Badge>
        )}
      </div>
    );
  };

  if (!profile || !['admin', 'superadmin'].includes(profile.role)) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">
            {t('access_denied', 'غير مسموح بالوصول', 'Access denied')}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              <CardTitle>{t('kyc_management', 'إدارة التحقق', 'KYC Management')}</CardTitle>
              <Badge variant="outline" className="text-xs">
                📊 Polling Mode
              </Badge>
            </div>
            <Button onClick={fetchKYCProfiles} size="sm" disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {t('refresh', 'تحديث', 'Refresh')}
            </Button>
          </div>
          <CardDescription>
            {t('review_kyc_documents', 'مراجعة وثائق التحقق المقدمة', 'Review and verify submitted KYC documents')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
              <p className="text-muted-foreground">{t('loading', 'جاري التحميل', 'Loading...')}</p>
            </div>
          ) : profiles.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">
                {t('no_pending_kyc', 'لا توجد طلبات تحقق معلقة', 'No pending KYC requests')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {profiles.map((userProfile) => (
                <Card key={userProfile.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div className="space-y-1 flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold truncate">{userProfile.full_name || 'Unnamed User'}</h3>
                            {getStatusBadge(userProfile.kyc_status)}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                            <div className="flex items-center gap-1 min-w-0">
                              <User className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{userProfile.email}</span>
                            </div>
                            {userProfile.phone && (
                              <div className="flex items-center gap-1">
                                <Phone className="h-3 w-3 flex-shrink-0" />
                                <span>{userProfile.phone}</span>
                              </div>
                            )}
                            {userProfile.national_id && (
                              <div className="flex items-center gap-1">
                                <CreditCard className="h-3 w-3 flex-shrink-0" />
                                <span>{userProfile.national_id}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3 flex-shrink-0" />
                              <span>{new Date(userProfile.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action buttons - now in a separate row to prevent overflow */}
                      <div className="flex flex-wrap gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewProfile(userProfile)}
                          className="flex-shrink-0"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          {t('review', 'مراجعة', 'Review')}
                        </Button>
                        
                        {/* Accept/Reject buttons for pending status */}
                        {(userProfile.kyc_status === 'PENDING' || userProfile.kyc_status === 'UNDER_REVIEW') && (
                          <>
                            <Button
                              size="sm"
                              disabled={updating === userProfile.id}
                              onClick={() => updateKYCStatus(userProfile.id, 'VERIFIED')}
                              className="bg-green-600 hover:bg-green-700 text-white flex-shrink-0"
                            >
                              {updating === userProfile.id ? (
                                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4 mr-1" />
                              )}
                              {t('accept', 'قبول', 'Accept')}
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              disabled={updating === userProfile.id}
                              onClick={() => updateKYCStatus(userProfile.id, 'REJECTED')}
                              className="flex-shrink-0"
                            >
                              {updating === userProfile.id ? (
                                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <X className="h-4 w-4 mr-1" />
                              )}
                              {t('reject', 'رفض', 'Reject')}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profile Details Modal */}
      {selectedProfile && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                KYC Review - {selectedProfile.full_name}
              </DialogTitle>
              <DialogDescription>
                Review the submitted documents and verify the user's identity
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* User Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Full Name</label>
                  <p className="text-sm text-muted-foreground">{selectedProfile.full_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <p className="text-sm text-muted-foreground">{selectedProfile.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Phone</label>
                  <p className="text-sm text-muted-foreground">{selectedProfile.phone || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">National ID</label>
                  <p className="text-sm text-muted-foreground">{selectedProfile.national_id || 'Not provided'}</p>
                </div>
              </div>

              {/* Documents */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Submitted Documents</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">National ID Front</label>
                    {selectedProfile.id_front_url ? (
                      <div className="border rounded-lg p-4 space-y-2">
                        <Badge variant="outline" className="text-green-600">Uploaded</Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewDocument(selectedProfile.id_front_url!)}
                          className="w-full"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Front Document
                        </Button>
                      </div>
                    ) : (
                      <div className="border rounded-lg p-4 text-center text-muted-foreground">
                        No document uploaded
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">National ID Back</label>
                    {selectedProfile.id_back_url ? (
                      <div className="border rounded-lg p-4 space-y-2">
                        <Badge variant="outline" className="text-green-600">Uploaded</Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewDocument(selectedProfile.id_back_url!)}
                          className="w-full"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Back Document
                        </Button>
                      </div>
                    ) : (
                      <div className="border rounded-lg p-4 text-center text-muted-foreground">
                        No document uploaded
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              {(selectedProfile.kyc_status === 'PENDING' || selectedProfile.kyc_status === 'UNDER_REVIEW') && (
                <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="destructive"
                    disabled={updating === selectedProfile.id}
                    onClick={() => updateKYCStatus(selectedProfile.id, 'REJECTED')}
                    className="w-full sm:w-auto"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Reject Application
                  </Button>
                  <Button
                    disabled={updating === selectedProfile.id}
                    onClick={() => updateKYCStatus(selectedProfile.id, 'VERIFIED')}
                    className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Approve & Verify
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default KYCManagement;
